import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { heating_circuits, heating_modes, interval_timetable, time_intervals, } from './data';
import { TimeLine } from './time-line/time-line';
import { intervalTimeTable2WeekSchedule } from './transform-schedule';
import { TemperatureInput } from './temperature-input';
import { ErrorBoundary } from 'shared/components/error-boundary';
import Schedule from './schedule';
import branding from 'shared/app/model/branding';
import { getScheduleMarkUpsOffsets } from './utils';
import { partial } from 'webui/utils/functions';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { Outlet, useOutletContext } from 'react-router';
import { useNavigateBack, useNavigateTo } from 'shared/hooks/router';
import { useConfirmDialog } from 'console/shared/hooks/ui';
export const WeekScheduleElementWithDefault = () => (_jsx(WeekScheduleElement, { interval_timetable: interval_timetable, time_intervals: time_intervals, heating_modes: heating_modes, selected_hc: heating_circuits[0], onChange: () => { }, is_boiler_zone: false, hm_id: 6546, validation_result: true }));
/**
 * @function
 * на вход дают IntervalTimeTable
 * смотрим на массив time_intervals
 * из всех доступных TimeInterval'ов делаем расписание на 7 дней
 * для времени, не попавшем в интервалы,
 * задаём дефолтное значение outside_default - температуру или режим
 * после редактирования получаем опять IntervalTimeTable
 * !!нужна защита от рекурсии в TimeInterval
 * @param props - {IntervalTimeTable}: интервальное расписание
 * @return ReactElement - заведует логикой расписания на 7 дней
 */
export const WeekScheduleElement = memo(function WeekScheduleElement(props) {
    const { interval_timetable, time_intervals, heating_modes, selected_hc, onChange, is_boiler_zone, hm_id, validation_result, } = props;
    const [schedule, setSchedule] = useState(intervalTimeTable2WeekSchedule(interval_timetable, time_intervals, heating_modes, is_boiler_zone));
    useEffect(() => {
        onChange(schedule, interval_timetable, time_intervals);
    }, [schedule]);
    const onChangeDefault = useCallback((temp_or_mode) => {
        setSchedule((sch) => ({ ...sch, temp_or_mode }));
    }, [setSchedule]);
    const onChangeDays = useCallback((day_data) => {
        setSchedule((sch) => ({
            ...sch,
            week: sch.week.map((d) => {
                const changed_day = day_data.find((day) => day.key === d.key);
                if (changed_day) {
                    return changed_day;
                }
                return d;
            }),
        }));
    }, [setSchedule]);
    const [open_editor, closeEditor, openEditor] = useBooleanState(false);
    const [ConfirmDialog, _openConfirmDialog] = useConfirmDialog();
    const goBack = useNavigateBack();
    const onClose = useCallback(() => {
        goBack();
        closeEditor();
    }, [closeEditor, goBack]);
    const [selectedDayId, setSelectedDayId] = useState(null);
    const go_to = useNavigateTo('intervalweek', { state: { item_id: hm_id } });
    const onSelectDay = useCallback((day_id) => {
        setSelectedDayId(day_id);
        openEditor();
        go_to();
    }, [setSelectedDayId, openEditor]);
    //@ts-ignore
    const is_validation_failed = validation_result?.time_intervals != null;
    const is_baxi_app = useMemo(() => branding.show_only_interval_timetable, [branding]);
    const time_lines = useMemo(() => schedule.week.map((day) => (_jsx(Box, { height: "100%", display: "flex", flexGrow: 1, boxSizing: "border-box", onClick: partial(onSelectDay, day.key), children: _jsx(ErrorBoundary, { children: _jsx(TimeLine, { day_intervals: day.day_intervals, temp_or_mode: schedule.temp_or_mode, heating_modes: heating_modes, week_view: true, is_baxi_app: is_baxi_app, selected_interval_id: null, selected_hc: selected_hc }) }) }, day.key))), [schedule, onSelectDay, heating_modes, selected_hc, is_baxi_app]);
    const schedule_labels = useMemo(() => {
        const hours_marks = ['00', '04', '08', '12', '16', '20', '24'];
        return getScheduleMarkUpsOffsets(hours_marks);
    }, []);
    return (_jsxs(ErrorBoundary, { children: [_jsxs(Box, { display: "flex", height: "100%", width: "100%", boxSizing: "border-box", flexDirection: "column", children: [is_validation_failed && (_jsx(Alert, { severity: "error", sx: { padding: '0', marginBottom: '5px' }, children: "\u0415\u0441\u0442\u044C \u043F\u0435\u0440\u0435\u0441\u0435\u0447\u0435\u043D\u0438\u0435 \u0441 \u0434\u0440\u0443\u0433\u0438\u043C\u0438 \u0438\u043D\u0442\u0435\u0440\u0432\u0430\u043B\u0430\u043C\u0438. \u0418\u0437\u043C\u0435\u043D\u0438\u0442\u0435 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438." })), _jsxs(Box, { display: "flex", alignItems: "flex-start", height: "100%", flexDirection: "column", width: is_baxi_app ? 'auto' : '100%', children: [_jsx(Box, { mb: 3, flexGrow: 1, width: "100%", children: _jsx(Schedule, { is_baxi_app: is_baxi_app, labels: schedule_labels, time_lines: time_lines }) }), _jsxs(Box, { flexShrink: 0, children: [_jsx(Box, { mb: 1, children: _jsxs(Typography, { variant: "subtitle1", color: "textSecondary", children: ["\u0417\u043D\u0430\u0447\u0435\u043D\u0438\u0435\u00A0", is_boiler_zone
                                                    ? 'режима '
                                                    : schedule.temp_or_mode.temp != null
                                                        ? 'температуры '
                                                        : 'режима ', "\u0432\u043D\u0435\u00A0\u0438\u043D\u0442\u0435\u0440\u0432\u0430\u043B\u043E\u0432"] }) }), _jsx(TemperatureInput, { heating_modes: heating_modes, temp_or_mode: schedule.temp_or_mode, onChange: onChangeDefault, is_boiler_mode: is_boiler_zone })] })] })] }), _jsx(Outlet, { context: {
                    open_editor,
                    onClose,
                    schedule,
                    is_baxi_app,
                    selectedDayId,
                    setSelectedDayId,
                    heating_modes,
                    onChangeDays,
                    selected_hc,
                    is_boiler_zone,
                    validation_result,
                } })] }));
});
export function useInterval() {
    return useOutletContext();
}
